
import { useListHeaderMenu } from '@/app/features/list-header-menu';
import GlobalSearchDialog from '@/app/features/reports/list-global-menu';
import useKeyboardNavigation from '@/app/features/reports/list-keyboard-navigation';
import { collapseSingleItemChains, flattenData, useFlattenedItems, useHeaderItems } from '@/app/features/reports/utils/flattenData';
import { cn } from '@udecode/cn';
import React, { createContext, startTransition, useCallback, useContext, useDeferredValue, useEffect, useImperativeHandle, useMemo, useRef, useState, useTransition } from 'react';
import { Virtuoso } from 'react-virtuoso';
import RowHeader from './components/report-group-header';
import RowContent from './components/report-row-content';
import useReportViewerContext, { useReportDisplayContext, useReportViewContext } from './components/report-viewer-context';
import { useReportGroupingContext } from './contexts/grouping-context';
import { ScrollPosition } from './types';
import { FlattenedItem, ReportItem } from './utils/entities';
import { Button } from '@/components/ui/button';
import { useReportSelectionContext } from '@/app/features/reports/contexts/selection-context';
import { Checkbox } from '@/components/ui/checkbox';
import { Transition } from '@headlessui/react';
import { useSticky } from './hooks/useSticky';
import { resolvePinnedHeader } from './utils/resolvePinnedHeader';
import { TOOLBAR_HEIGHT, HEADER_HEIGHT, SDA_COLUMN_HEADINGS_HEIGHT } from './constants';

import { ScrollContext } from './contexts/scroll-context';

export { TOOLBAR_HEIGHT };
const SCROLL_OFFSET = -TOOLBAR_HEIGHT - HEADER_HEIGHT;

function calculateScrollOffset(_index: number) {
    return -TOOLBAR_HEIGHT;
}

export const CustomItem = ({ children, item, context, ...props }: CustomItemProps) => {

    const { ref, isVisible, intersectionTop } = useSticky(context.stickyTop);
    // const isHeader = isItemSticky(item);
    const isHeader = !item.content;
    //const { scrollDirection } = context;

    useEffect(() => {
        if (isHeader) return;
        if (!isVisible) return;
        console.debug('Saw content', { isVisible, intersectionTop, item });
        startTransition(() => {
            context.setLastSeenContent(item);
        });
    }, [isVisible, intersectionTop, item]);

    const stickyTop = context.stickyTop;

    useEffect(() => {

        if (intersectionTop > stickyTop + HEADER_HEIGHT) {
            return;
        }

        if (!isHeader) {
            console.debug('Content not a header', { isVisible, isHeader, intersectionTop, item });
            return;
        };

        // ROOT MARGIN up
        // TOOLBAR HEIGHT down.

        if (isVisible) {
            console.debug('Header: ', item.parents?.join(' > ') || item.title, { isVisible, isHeader, intersectionTop, item });
            // Promote only once the header has actually reached its own sticky point
            // (intersectionTop <= stickyTop), not merely within HEADER_HEIGHT of it. The wider
            // +HEADER_HEIGHT window let a header be selected into the reserved pinned-header-slot
            // while its own natural (not-yet-stuck) position was still up to 44px below stickyTop
            // — showing the reserved slot's copy simultaneously with the real, still-in-flow
            // header instance, rendered ~44-50px apart (raytd/web QA 108.03 item 18, persisting).
            if (intersectionTop <= stickyTop) {
                context.setLastSelectedHeader(item);
            }
        } else {
            //42 is the height of the header
            if (intersectionTop > 0 && intersectionTop <= stickyTop + HEADER_HEIGHT) {
                console.debug('Header: UP!', { isHeader, intersectionTop, stickyTop });
                context.setLastSelectedHeader(item);
            }

        }
    }, [isVisible, intersectionTop, stickyTop]);

    if (isHeader) {
        return (
            <div
                ref={ref} // Use our internal ref for the observer
                {...props} // Spread props from Virtuoso (contains the all-important props.style)
                style={{
                    ...props.style, // CRITICAL: Keep Virtuoso's positioning styles
                    position: 'sticky', // Add our sticky styles
                    top: stickyTop,
                    zIndex: 40,
                    backgroundColor: 'white',
                    // visibility: isPinned ? 'hidden' : 'visible',
                }}
            >
                {children}
            </div>
        );
    }

    // For non-sticky items, just spread the props. No ref is needed.
    return <div {...props} ref={ref}>{children}</div>;
}

interface CustomItemContext {
    pinnedHeaderId: string;
    scrollDirection: 'up' | 'down';
    setLastSelectedHeader: React.Dispatch<React.SetStateAction<FlattenedItem | null>>;
    setLastSeenContent: React.Dispatch<React.SetStateAction<FlattenedItem | null>>;
    // TOOLBAR_HEIGHT, plus SDA_COLUMN_HEADINGS_HEIGHT when the sticky column-heading row is
    // also present (raytd/web QA 108.03 item 18) — so the group header sticks flush below it
    // instead of overlapping/leaving a gap.
    stickyTop: number;
}

interface CustomItemProps {
    children?: React.ReactNode;
    item: FlattenedItem;
    context: CustomItemContext;
    'data-index': number;
    [key: string]: any;
}

function findTestIdInPath(items: ReportItem[], testId: number): string[] {
    const path: string[] = [];

    const find = (items: ReportItem[]): boolean => {
        for (const item of items) {
            // Check if this item contains the test in its content
            if (item.content?.some(content => content.id === String(testId))) {
                path.push(item.id);
                return true;
            }

            // Check children if they exist
            if (item.children && find(item.children)) {
                path.push(item.id);
                return true;
            }
        }
        return false;
    };

    find(items);
    return path.reverse(); // Reverse to get root-to-leaf order
}

function findIdInPath(items: ReportItem[], id: string): string[] {
    const path: string[] = [];

    const find = (items: ReportItem[]): boolean => {
        for (const item of items) {
            if (item.id === id) {
                path.push(item.id);
                return true;
            }

            if (item.children && find(item.children)) {
                path.push(item.id);
                return true;
            }
        }
        return false;
    };

    find(items);
    return path.reverse(); // Reverse to get root-to-leaf order
}

interface VirtualizedNestedListProps {
    width?: number,
    height?: number,
    setIsScrolling: (scrolling: boolean) => void;
    setScrollPosition: (position: ScrollPosition) => void;
    scrollParent?: HTMLDivElement;
}

const VirtualizedNestedList = React.forwardRef<HTMLDivElement, VirtualizedNestedListProps>(({
    width, height,
    setIsScrolling,
    setScrollPosition,
    scrollParent
}, ref) => {

    const [scrollDirection, setScrollDirection] = useState<'up' | 'down'>('down');
    const lastScrollTop = useRef(0);
    const [scrollContainer, setScrollContainer] = useState<HTMLDivElement | null>(null);
    const internalRef = useRef<any>(null);

    const [isPending, startTransition] = useTransition();

    const { expandedItems: expandedIds, setExpandedItems: setExpandedIds } = useReportGroupingContext();
    const { setUpdatingTest } = useReportViewerContext();

    const {
        data,
        filteredData,
        filters,
        groupMode,
        viewId,
        isSdaFormat,
    } = useReportViewContext();

    const isPrintMode = viewId === 'print';
    const { headerMode } = useReportDisplayContext();

    // SdaResultsColumnHeadings (rendered by the parent, outside this component) is itself
    // sticky at TOOLBAR_HEIGHT for SDA formats — push the group headers' sticky offset down by
    // its height so the two stack flush instead of overlapping (raytd/web QA 108.03 item 18).
    const stickyTop = TOOLBAR_HEIGHT + (isSdaFormat && !isPrintMode ? SDA_COLUMN_HEADINGS_HEIGHT : 0);

    const [pinnedHeader, setPinnedHeader] = useState(null);

    const [placeHolderHeight, setPlaceHolderHeight] = useState(1);

    ///255 254 is the fixed header scroll height
    const bottomOfFixedHeader = 254;
    const topOfFixedHeader = 100;

    useEffect(() => {
        const handleScroll = () => {
            const scrollTop = window.pageYOffset;
            console.debug('scrollTop', scrollTop);

            if (Math.abs(scrollTop - lastScrollTop.current) < 5) return; // threshold to avoid jitter


            // if (scrollTop > topOfFixedHeader && scrollTop < bottomOfFixedHeader) {
            //     const progress = scrollTop / 254;
            //     const clampedProgress = Math.min(Math.max(progress, 0), 1);
            //     //const newSize = Math.round(clampedProgress * HEADER_HEIGHT);
            //     startTransition(() => {
            //         setPlaceHolderHeight(clampedProgress);
            //     });
            // }

            if (scrollTop > lastScrollTop.current) {
                setScrollDirection('down');
            } else if (scrollTop < lastScrollTop.current) {
                setScrollDirection('up');
                if (scrollTop < topOfFixedHeader) {
                    startTransition(() => {
                        setPinnedHeader(null);
                    });
                }
            }
            lastScrollTop.current = scrollTop <= 0 ? 0 : scrollTop;
        };

        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    useEffect(() => {
        console.debug('placeHolderHeight', placeHolderHeight);
    }, [placeHolderHeight]);

    useEffect(() => {
        if (scrollParent) {
            setScrollContainer(scrollParent);
        }
    }, [scrollParent]);

    // Interim headers of a single-item chain are hidden in the interactive list (raytd/web#232);
    // print/PDF keep every header.
    const flattenedItems = useFlattenedItems(data, expandedIds, groupMode, { collapseSingleItemChains: !isPrintMode });

    // Clear sticky indices when flattened items change significantly
    // This prevents stale index references after data updates
    useEffect(() => {
        // Reset sticky indices and last max index when the data structure changes
        console.debug('Data changed, clearing sticky indices and resetting lastMaxIndex');
    }, [flattenedItems.length]);

    const scrollToHeader = useCallback((id: string, level?: number) => {

        console.debug('scrollToHeader ref', { id, level });
        //split the id into an array and return the first level parts

        //check to see if the id is in the expandedIds if not, we will need to expand all headers in the path;
        const path = findIdInPath(data, id);
        // path.forEach(groupId => {
        //     console.debug('expanding path', { groupId, path });
        //     // expandedIds.add(groupId);
        // });

        // Expand all groups in the path
        const newExpandedIds = new Set(expandedIds);
        path.forEach(groupId => {
            newExpandedIds.add(groupId);
        });

        // Update expanded items if there are new items to expand
        if (path.length > 0) {
            setExpandedIds(newExpandedIds);

            // Use requestAnimationFrame to wait for next render
            requestAnimationFrame(() => {
                const idParts = id.split('.');
                const firstLevelParts = level ? idParts.slice(0, level + 1).join('.') : idParts.join('.');
                let index = flattenedItems.findIndex(item => item.id === firstLevelParts);
                if (index === -1) {
                    // The target is an interim header hidden by single-item-chain collapse: land on
                    // the first visible header beneath it (the chain's ultimate header).
                    index = flattenedItems.findIndex(item => !item.content && item.id.startsWith(`${firstLevelParts}.`));
                }
                if (index !== -1) {
                    console.debug('scrollToHeader item', { index, id, firstLevelParts, item: flattenedItems[index] });
                    internalRef.current?.scrollToIndex({ index: index, align: 'start', offset: calculateScrollOffset(index) });
                }
            });
        }


    }, [flattenedItems, expandedIds]);

    const scrollToTest = useCallback((id: number) => {
        // Find the path to the test
        console.debug('scrollToTest', { flattenedItems, expandedIds });

        const index = flattenedItems.findIndex(item => item.id === String(id));
        if (index >= 0) {
            console.debug('scrollToTest index 1', { index, id });
            internalRef.current?.scrollToIndex({ index: index, align: 'start', offset: calculateScrollOffset(index) });
            return;
        }

        const path = findTestIdInPath(data, id);

        // Expand all groups in the path
        const newExpandedIds = new Set(expandedIds);
        path.forEach(groupId => {
            newExpandedIds.add(groupId);
        });

        // Update expanded items if there are new items to expand
        if (path.length > 0) {
            setExpandedIds(newExpandedIds);

            // Use requestAnimationFrame to wait for next render
            requestAnimationFrame(() => {
                const rows = flattenData(data, 0, groupMode, headerMode, newExpandedIds);
                const index = (isPrintMode ? rows : collapseSingleItemChains(rows)).findIndex(item => item.id === String(id));
                console.debug('scrollToTest index 2', { index, id });
                if (index !== -1) {
                    internalRef.current?.scrollToIndex({ index: index, align: 'start', offset: calculateScrollOffset(index) });
                }
            });
        }
    }, [data, expandedIds, setExpandedIds, flattenedItems, groupMode, headerMode, isPrintMode]);

    useEffect(() => {
        console.debug('flattenedItems', flattenedItems);
        if (setUpdatingTest) {
            setUpdatingTest(null);
        }
    }, [flattenedItems, setUpdatingTest]);

    const toggleExpand = useCallback((id: string) => {
        const next = new Set(expandedIds);
        if (next.has(id)) {
            next.delete(id);
        } else {
            next.add(id);
        }

        startTransition(() => {
            if (next.size === 0) {
                // setStickyHeaderItem(null);
                // scrollParent.scrollTop = 0;
            }
            setExpandedIds(next);
        });
    }, [expandedIds, setExpandedIds]);

    useEffect(() => {
        startTransition(() => {
            if (expandedIds.size === 0) {
                setPinnedHeader(null);
                setLastSelectedHeader(null);
                setLastSeenContent(null);
            }
        });
    }, [expandedIds]);

    // Expose specific methods/properties to parent
    useImperativeHandle(ref, () => {
        // Cast to HTMLDivElement to match expected type
        const element = internalRef.current as unknown as HTMLDivElement;
        return {
            ...element, // Spread all HTMLDivElement properties
            scrollToIndex: (index: number) => {
                internalRef.current?.scrollToIndex(index);
            },
            scrollToTest: (id: number) => {
                scrollToTest(id);
            },
            customMethod: () => {
                // Do something with internalRef
            }
        };
    }, [scrollToTest]);


    // Debounce the sticky indices updates to handle fast scrolling
    const [lastSelectedHeader, setLastSelectedHeader] = useState<FlattenedItem | null>(null);

    const deferredLastSelectedHeader = useDeferredValue(lastSelectedHeader);

    const [lastSeenContent, setLastSeenContent] = useState<FlattenedItem | null>(null);
    const deferredLastSeenContent = useDeferredValue(lastSeenContent);

    const listOfHeaders = useMemo(() => {
        return flattenedItems
            .map((item, index) => !item.content ? { item, index } : null)
            .filter((header): header is { item: FlattenedItem, index: number } => !!header)
    }, [flattenedItems]);

    const virtuosoContext = useMemo(() => ({
        pinnedHeaderId: pinnedHeader?.id,
        scrollDirection,
        // Pass the state setter function down
        setLastSelectedHeader,
        setLastSeenContent,
        stickyTop,
    }), [pinnedHeader?.id, scrollDirection, stickyTop]);

    const scrollDirectionRef = useRef(scrollDirection);

    // This effect keeps the ref updated with the latest scrollDirection
    // without causing extra re-renders of components that use the ref.
    useEffect(() => {
        scrollDirectionRef.current = scrollDirection;
    }, [scrollDirection]);

    useEffect(() => {
        console.debug('lastSelectedHeader updated:', deferredLastSelectedHeader?.parents?.join(' > ') || deferredLastSelectedHeader?.title);
        const next = resolvePinnedHeader(listOfHeaders, deferredLastSelectedHeader, scrollDirectionRef.current);
        startTransition(() => setPinnedHeader(next));
    }, [deferredLastSelectedHeader, listOfHeaders]);

    const previousLastSeenContent = useRef<FlattenedItem | null>(null);

    useEffect(() => {
        if (previousLastSeenContent.current?.id !== lastSeenContent?.id) {
            previousLastSeenContent.current = lastSeenContent;
            console.debug('lastSeenContent updated:', lastSeenContent?.groupHeaderId, lastSeenContent?.parents?.join(' > ') || lastSeenContent?.title, { lastSeenContent, deferredLastSeenContent, pinnedHeader, scrollDirectionRef: scrollDirectionRef.current });
            const isPinnedDifferent = pinnedHeader?.id !== lastSeenContent?.groupHeaderId;
            if (isPinnedDifferent && scrollDirectionRef.current === 'up') {
                // setPinnedHeader(lastSeenContent);
                console.debug('pinnedHeader different from lastSeenContent', { pinnedHeader, lastSeenContent });
                startTransition(() => {
                    setPinnedHeader(lastSeenContent);
                });
            }
        }
    }, [deferredLastSeenContent, pinnedHeader]);

    useEffect(() => {

        //get the index of the lastSeenContent
        const index = flattenedItems.findIndex(item => item.id === deferredLastSeenContent?.id);
        if (index !== -1) {
            // internalRef.current?.scrollToIndex({ index: index, align: 'start', offset: calculateScrollOffset(index) });
        }
        console.debug('lastSeenContent updated:', { index, deferredLastSeenContent });
    }, [deferredLastSeenContent, flattenedItems]);


    // const { open, setOpen, GlobalSearchDialog } = useGlobalSearch({ flattenedItems, scrollToHeader });

    const getNextIndex = useCallback((currentIndex: number, mode: 'content' | 'header') => {
        // Search forward from currentIndex + 1
        for (let i = currentIndex + 1; i < flattenedItems.length; i++) {
            const item = flattenedItems[i];
            if (mode === 'content' && item.content) {
                return i;
            } else if (mode === 'header' && !item.content && item.hasContent) {
                return i;
            }
        }
        return -1; // Not found
    }, [flattenedItems]);

    const getPreviousIndex = useCallback((currentIndex: number, mode: 'content' | 'header') => {
        // Search backward from currentIndex - 1
        for (let i = currentIndex - 1; i >= 0; i--) {
            const item = flattenedItems[i];
            if (mode === 'content' && item.content) {
                return i;
            } else if (mode === 'header' && !item.content && item.hasContent) {
                return i;
            }
        }
        return -1; // Not found
    }, [flattenedItems]);

    const getCurrentIndex = useCallback(() => {
        console.debug('currentIndex', { deferredLastSeenContent, flattenedItems });
        return flattenedItems.findIndex(item => item.id === deferredLastSeenContent?.id);
    }, [deferredLastSeenContent, flattenedItems]);

    const navigateToIndex = useCallback((index: number) => {
        internalRef.current?.scrollToIndex({ index: index, align: 'start', offset: SCROLL_OFFSET });
        setLastSeenContent(flattenedItems[index]);
    }, [internalRef, flattenedItems, setLastSeenContent]);

    // const { scrollerRef } = useKeyboardNavigation({ getNextIndex, getPreviousIndex, getCurrentIndex, navigateToIndex });

    const headerItems = useHeaderItems(data, groupMode);

    const { setMenu, DropdownMenu } = useListHeaderMenu({ flattenedItems: headerItems, scrollToHeader });

    const { selectAll, deselectAll, selectedItems, bulkEditMode } = useReportSelectionContext();

    console.debug('selectedItems', selectedItems, data);

    return (
        <ScrollContext.Provider value={{ scrollToHeader, setMenu }}>
            <div style={{ position: 'relative' }}>
                {/* 1. The Pinned Header (Never Virtualized) — collapsed to 0 height until there's
                    something to show in it (a pinned header once scrolled past one, or the bulk-
                    edit select-all checkbox). Reserving its full 44px unconditionally left a
                    permanent empty gap above the list on every load (raytd/web QA 108.03, item
                    14), most visible in the SDA view since its hoisted column-heading row sits
                    directly above it with nothing else to visually absorb the gap. */}
                <div
                    data-testid="pinned-header-slot"
                    className='transition-all duration-100 ease-in-out'
                    style={{
                        position: 'sticky',
                        height: pinnedHeader || bulkEditMode ? 44 * placeHolderHeight : 0, // This actually changes the layout height
                        top: stickyTop,
                        zIndex: 20,
                        backgroundColor: 'white',
                        overflow: 'hidden' // Hide content when height is small
                    }}>
                    {pinnedHeader ? (
                        <RowHeader
                            item={pinnedHeader}
                            toggleExpand={toggleExpand}
                            active={true} // Maybe add a style to show it's the main active one
                        />
                    ) : (
                        <div
                            className="flex items-center h-full"
                        >
                            <Transition
                                show={bulkEditMode}
                                enter="transition-opacity duration-100"
                                enterFrom="opacity-0"
                                enterTo="opacity-100"
                                leave="transition-opacity duration-100"
                                leaveFrom="opacity-100"
                                leaveTo="opacity-0"
                            >
                            <Checkbox
                                checked={
                                    selectedItems.size === filteredData.length
                                        ? true
                                        : selectedItems.size > 0
                                        ? 'indeterminate'
                                        : false
                                }
                                title="Select All"
                                onCheckedChange={() => {
                                    if (selectedItems.size === filteredData.length) {
                                        deselectAll()
                                    } else {
                                        selectAll()
                                        }
                                    }}
                                />
                                </Transition>
                        </div>
                    )
                    }
                </div>

                {isPrintMode ? (
                    // In print mode, render all items without virtualization
                    <div className="print-all-items">
                        {flattenedItems.map((item) => {
                            if (!item.content) {
                                return <RowHeader key={item.id} item={item} toggleExpand={toggleExpand} active={false} />;
                            } else {
                                return <RowContent key={item.id} item={item} />;
                            }
                        })}
                    </div>
                ) : (
                    <Virtuoso
                        useWindowScroll={true}
                        ref={internalRef}
                        style={{ flex: 1 }}
                        isScrolling={setIsScrolling}
                        overscan={20}
                        customScrollParent={scrollContainer}
                        data={flattenedItems}
                        context={virtuosoContext}
                        // scrollerRef={scrollerRef}
                        components={{
                            Item: CustomItem,
                        }}
                        itemContent={(index, item) => {

                            if (!item.content) {
                                return <RowHeader item={item} toggleExpand={toggleExpand} active={false} />;
                            } else {
                                const isActiveItem = item.id === deferredLastSeenContent?.id;

                                return <RowContent
                                    item={item}
                                //className={cn("border-r-2 border-r-transparent", isActiveItem && "border-r-zinc-300")}
                                />;
                            }
                        }}
                        computeItemKey={(index, item) => {
                            return `${item.id}`;
                        }}
                    />
                )}
            </div>
            {DropdownMenu}
            <GlobalSearchDialog
                flattenedItems={headerItems}
            />
        </ScrollContext.Provider>
    );

});

export default VirtualizedNestedList;